/**
 * WorkspaceLayout — redesigned shell (EM design handoff).
 * Page header + live timer, content Outlet, and a floating frosted bottom
 * tab bar (icons reveal their label when active).
 */
import React from 'react';
import { NavLink, Outlet, useResolvedPath, useMatch, useLocation } from 'react-router-dom';
import { LayoutDashboard, FolderKanban, ListChecks, Building2, Timer, PieChart, PenTool, Users } from 'lucide-react';
import { isManager, wsBase } from './wsApi';
import { T } from '../../components/em/ui';
import TimerWidget from './TimerWidget';

const TABS = [
  { path: '',           label: 'Overview',   Icon: LayoutDashboard, end: true,  managerOnly: false,
    title: 'Workspace',  desc: 'Projects, tasks, time and profitability — all in one place' },
  { path: 'projects',   label: 'Projects',   Icon: FolderKanban,    end: false, managerOnly: false,
    title: 'Projects',   desc: 'Plan, track and deliver client work' },
  { path: 'tasks',      label: 'My Tasks',   Icon: ListChecks,      end: false, managerOnly: false,
    title: 'My Tasks',   desc: 'Everything assigned to you, in one place' },
  { path: 'timesheets', label: 'Timesheets', Icon: Timer,           end: false, managerOnly: false,
    title: 'Timesheets', desc: 'Log time and review hours across people and projects' },
  { path: 'canvas',     label: 'Canvas',     Icon: PenTool,         end: false, managerOnly: false,
    title: 'Canvas',     desc: 'Whiteboards and diagrams for your projects' },
  { path: 'clients',    label: 'Clients',    Icon: Building2,       end: false, managerOnly: true,
    title: 'Clients',    desc: 'Companies you bill and deliver for' },
  { path: 'teams',      label: 'Teams',      Icon: Users,           end: false, managerOnly: true,
    title: 'Teams',      desc: 'Group people to view their combined timesheets' },
  { path: 'analytics',  label: 'Analytics',  Icon: PieChart,        end: false, managerOnly: true,
    title: 'Analytics',  desc: 'Profitability, utilisation and trends' },
];

// Dark-pill hover tooltip (mirrors the Canvas Studio toolbar's data-tip idiom).
// Only rendered for the icon-only (inactive) tabs — the active tab already
// shows its label inline, so it doesn't need one.
const TAB_TOOLTIP_CSS = `
.ws-tab { position: relative; }
.ws-tab[data-tip]::after {
  content: attr(data-tip);
  position: absolute; bottom: calc(100% + 10px); left: 50%;
  transform: translateX(-50%) translateY(4px);
  background: #1f2937; color: #fff; font: 600 11px 'Inter', sans-serif;
  padding: 4px 8px; border-radius: 6px; white-space: nowrap;
  opacity: 0; pointer-events: none; transition: opacity 0.12s ease, transform 0.12s ease;
}
.ws-tab[data-tip]:hover::after { opacity: 1; transform: translateX(-50%) translateY(0); }
`;

function FloatingTab({ to, end, label, Icon }) {
  const resolved = useResolvedPath(to);
  const match = useMatch({ path: resolved.pathname, end });
  const on = !!match;
  return (
    <NavLink to={to} end={end} className="ws-tab" data-tip={on ? undefined : label} aria-label={label} style={{
      display: 'inline-flex', alignItems: 'center', gap: on ? 8 : 0, height: 44,
      padding: on ? '0 18px 0 16px' : '0 13px', borderRadius: 9999, textDecoration: 'none',
      background: on ? T.gradient : 'transparent',
      color: on ? '#fff' : T.fg5,
      boxShadow: on ? '0 4px 10px -3px rgba(245,75,1,0.5)' : 'none',
      transition: 'gap 220ms cubic-bezier(0.16,1,0.3,1), padding 220ms cubic-bezier(0.16,1,0.3,1), background 200ms, color 200ms',
    }}>
      <Icon size={18} style={{ flexShrink: 0 }} />
      <span style={{
        font: `600 13px ${T.font}`, whiteSpace: 'nowrap', overflow: 'hidden',
        maxWidth: on ? 120 : 0, opacity: on ? 1 : 0,
        transition: 'max-width 220ms cubic-bezier(0.16,1,0.3,1), opacity 180ms ease',
      }}>{label}</span>
    </NavLink>
  );
}

export default function WorkspaceLayout() {
  const manager = isManager();
  const base = wsBase();
  const location = useLocation();
  const tabs = TABS.filter((t) => !t.managerOnly || manager);

  // Which tab does the current URL belong to? (longest path prefix wins)
  const rel = location.pathname.startsWith(base) ? location.pathname.slice(base.length).replace(/^\//, '') : '';
  const active = TABS.slice().sort((a, b) => b.path.length - a.path.length)
    .find((t) => (t.path ? rel === t.path || rel.startsWith(`${t.path}/`) : rel === '')) || TABS[0];
  // A single project page has its own header card — hide the big page heading there.
  const onProjectDetail = /^projects\/\d+/.test(rel);

  return (
    <div style={{ maxWidth: 1440, margin: '0 auto', padding: '0 32px 120px', position: 'relative' }}>
      {/* Header */}
      {onProjectDetail ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '20px 0 14px' }}>
          <TimerWidget />
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '28px 0 22px', flexWrap: 'wrap' }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: T.orange50, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <active.Icon size={24} color={T.orange} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h1 style={{ margin: 0, font: `700 28px/34px ${T.font}`, letterSpacing: '-0.025em', color: T.fg1 }}>{active.title}</h1>
            <p style={{ margin: '3px 0 0', font: `400 13px ${T.font}`, color: T.fg5 }}>{active.desc}</p>
          </div>
          <TimerWidget />
        </div>
      )}

      <Outlet />

      {/* Floating bottom tab bar */}
      <style>{TAB_TOOLTIP_CSS}</style>
      <div style={{ position: 'fixed', bottom: 22, left: 0, right: 0, zIndex: 60, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, padding: 6, borderRadius: 9999, pointerEvents: 'auto',
          background: 'rgba(255,255,255,0.82)',
          backdropFilter: 'blur(22px) saturate(180%)', WebkitBackdropFilter: 'blur(22px) saturate(180%)',
          border: '1px solid rgba(255,255,255,0.7)',
          boxShadow: '0 2px 8px rgba(10,10,10,0.06), 0 16px 40px -12px rgba(10,10,10,0.28), inset 0 1px 0 rgba(255,255,255,0.9)',
        }}>
          {tabs.map((t) => (
            <FloatingTab key={t.path || 'overview'} to={t.path ? `${base}/${t.path}` : base} end={t.end} label={t.label} Icon={t.Icon} />
          ))}
        </div>
      </div>
    </div>
  );
}
